import {
    AdProvider,
    type AdProviderConfig,
    AdProviderStatus,
    type OutOfPageAdType,
    type PageAdOptions,
    type PageAdSize,
    type PageAdType,
} from '@/modules/adv'
import type { Banner } from '@/modules/adv/banner'
import type { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { ImageBanner } from '@/modules/adv/providers/direct/image-banner'
import { VideoBanner } from '@/modules/adv/providers/direct/video-banner'
import type { PlatformType } from '@/types'
import type { EventForLoggersType } from '@/types/logger.types'

import type { Creative, Target } from './types'

function sendRequest(url: string | undefined) {
    if (url) {
        fetch(url, {
            method: 'GET',
            mode: 'no-cors',
        }).catch(() => {
            // Ignore errors
        })
    }
}

export class DirectAdsProvider implements AdProvider {
    readonly serviceStatus: Promise<AdProviderStatus>

    private readonly imageBanner: ImageBanner
    private readonly videoBanner: VideoBanner

    private targeting: Record<string, string | string[]> = {}
    private readonly platform: PlatformType
    private readonly logEvent: (event: EventForLoggersType) => void
    private data: Target[] | null = null
    private roundRobinIndexes: Map<string, number> = new Map()

    constructor({
        targeting,
        platform,
        footerHeight,
        logEvent,
        locale,
    }: AdProviderConfig & { footerHeight?: string; logEvent: (event: EventForLoggersType) => void; locale: string }) {
        this.targeting = { ...targeting }
        this.platform = platform
        this.logEvent = logEvent

        this.imageBanner = new ImageBanner({
            logEvent: this.logEvent,
            getAdUnit: this.getAdUnit.bind(this),
            sendRequest,
            getCreative: this.getCreative.bind(this),
        })

        this.videoBanner = new VideoBanner({
            logEvent: this.logEvent,
            getAdUnit: this.getAdUnit.bind(this),
            sendRequest,
            getCreative: this.getCreative.bind(this),
            footerHeight,
            locale,
        })

        this.serviceStatus = this.loadResources()
    }

    private async loadResources(): Promise<AdProviderStatus> {
        try {
            const { targets } = await import('./config')
            this.data = targets
            await this.videoBanner.ready
            return AdProviderStatus.online
        } catch {
            return AdProviderStatus.offline
        }
    }

    private getCreative(type: OutOfPageAdType | PageAdType, sizes?: PageAdSize[]): Creative | null {
        if (!this.data) {
            return null
        }

        let creatives = this.data
            .filter((t) => t.platforms.includes(this.platform))
            .flatMap((c) => c.creatives)
            .filter((c) => c.adUnits.includes(type))

        if (sizes?.length) {
            creatives = creatives.filter(
                (c) =>
                    !c.width ||
                    !c.height ||
                    sizes.some((size) => size === 'fluid' || (c.width === size[0] && c.height === size[1])),
            )
        }

        if (creatives.length === 0) {
            return null
        }

        const key = `${this.platform}-${type}`
        const currentIndex = this.roundRobinIndexes.get(key) ?? Math.floor(Math.random() * creatives.length)

        const nextIndex = (currentIndex + 1) % creatives.length
        this.roundRobinIndexes.set(key, nextIndex)

        return creatives[currentIndex]
    }

    private getAdUnit(type: OutOfPageAdType | PageAdType, creative?: Creative) {
        if (creative?.file) {
            const campaignId = this.data?.find((target) =>
                target.creatives.some((c) => c.file === creative.file),
            )?.campaignId

            if (campaignId) {
                return `${this.platform}-${type}-${campaignId}-${creative.file}`
            }
        }

        return `${this.platform}-${type}`
    }

    prepareOutOfPageAd(type: OutOfPageAdType, _?: number): Promise<OutOfPageBanner> {
        return this.videoBanner.requestAd(type)
    }

    requestPageAd(options: PageAdOptions): Promise<Banner> {
        return this.imageBanner.requestAd(options)
    }

    updateTargeting(targeting: AdProviderConfig['targeting'] = {}) {
        Object.entries(targeting).forEach(([key, value]) => {
            if (value) {
                this.targeting[key] = value
            } else if (this.targeting[key]) {
                delete this.targeting[key]
            }
        })
    }

    getAdUnitByType(type: OutOfPageAdType | PageAdType) {
        return this.getAdUnit(type)
    }
}
